import React from "react";
import {
  Beaker,
  Microscope,
  GraduationCap,
  BookOpen,
  Zap,
  Building2,
  Recycle,
  Award,
  Star,
  Trophy,
} from "lucide-react";
import { awardCategories } from "../data/mock";

const iconMap = {
  Beaker,
  Microscope,
  GraduationCap,
  BookOpen,
  Zap,
  Building2,
  Recycle,
  Award,
  Star,
};

const tierStyles = {
  gold: { grad: "from-[#FFB84A] to-[#F59E0B]", ring: "#F59E0B", label: "Gold Tier" },
  silver: { grad: "from-[#C0C7CE] to-[#8B99A5]", ring: "#8B99A5", label: "Silver Tier" },
  platinum: { grad: "from-[#1F5943] to-[#0f2a20]", ring: "#1F5943", label: "Platinum Tier" },
};

export default function AwardCategories() {
  return (
    <section id="awards" className="relative py-24 bg-[#0f2a20] text-white overflow-hidden">
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-[#1F5943]/30 blur-3xl" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full bg-[#F59E0B]/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 text-white mb-5 backdrop-blur-sm">
            <Trophy className="w-4 h-4 text-[#F59E0B]" />
            <span className="text-xs font-bold tracking-widest uppercase">The Awards</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl mb-4">
            Nine categories.
            <br />
            <span className="text-[#4ADE80]">Countless changemakers.</span>
          </h2>
          <p className="text-white/70 text-lg leading-relaxed">
            From individual scientists to whole organisations — recognition for
            every level of leadership in sustainable science.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {awardCategories.map((a, i) => {
            const Icon = iconMap[a.icon] || Award;
            const tier = tierStyles[a.tier];
            return (
              <div
                key={a.title}
                className="relative p-6 rounded-2xl bg-white/[0.03] border border-white/10 hover:bg-white/[0.06] hover:border-white/20 transition-all duration-500 group overflow-hidden"
              >
                <div
                  className={`absolute -top-16 -right-16 w-40 h-40 rounded-full bg-gradient-to-br ${tier.grad} opacity-10 group-hover:opacity-25 transition-opacity`}
                />

                <div className="relative flex items-start justify-between mb-6">
                  <div
                    className={`w-14 h-14 rounded-xl bg-gradient-to-br ${tier.grad} flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500`}
                  >
                    <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                  </div>
                  <span
                    className="text-[10px] font-bold tracking-widest uppercase px-2.5 py-1 rounded-full border"
                    style={{ borderColor: tier.ring + "66", color: tier.ring }}
                  >
                    {tier.label}
                  </span>
                </div>

                <h3 className="font-display text-xl leading-tight mb-2 group-hover:text-[#4ADE80] transition-colors">
                  {a.title}
                </h3>
                <div className="text-xs text-white/50 font-mono">
                  Category #{String(i + 1).padStart(2, "0")}
                </div>

                <div className="mt-5 pt-5 border-t border-white/10 flex items-center justify-between text-xs text-white/60">
                  <span>Open for nominations</span>
                  <div className="w-2 h-2 rounded-full bg-[#4ADE80] animate-pulse" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
